import { elementVisibleInMode, type SchemaData, type SchemaStation } from '../schema/types'

export interface LineSlugGraph {
  /** One representative station record per slug on the line (first in schema order). */
  readonly stationBySlug: ReadonlyMap<string, SchemaStation>
  /** Undirected adjacency between the line's station slugs, from its connections. */
  readonly neighbours: ReadonlyMap<string, ReadonlySet<string>>
}

/**
 * The line's stations collapsed to slugs, joined by the line's connections.
 * A slug can carry several platform records (Euston has two Northern
 * records); the line view lists a station once, so the graph is by slug.
 * Stations without a slug are left out — they have no page to open. The
 * line view is the day line: a night-only stop or hop (a late-night train on
 * another line's tracks, TASK-1136 — `modes: ["night"]`) is left out too.
 */
export function lineSlugGraph(schema: SchemaData, lineId: string): LineSlugGraph {
  const stationBySlug = new Map<string, SchemaStation>()
  const slugById = new Map<string, string>()
  for (const station of schema.stations) {
    if (station.line.id !== lineId || station.slug === undefined || station.slug === '') continue
    if (!elementVisibleInMode(station.modes, 'normal')) continue
    slugById.set(station.id, station.slug)
    if (!stationBySlug.has(station.slug)) stationBySlug.set(station.slug, station)
  }
  const neighbours = new Map<string, Set<string>>(
    [...stationBySlug.keys()].map((slug) => [slug, new Set<string>()]),
  )
  for (const connection of schema.connections) {
    if (connection.lineId !== lineId || !elementVisibleInMode(connection.modes, 'normal')) continue
    const a = slugById.get(connection.fromStationId ?? '')
    const b = slugById.get(connection.toStationId ?? '')
    if (a === undefined || b === undefined || a === b) continue
    neighbours.get(a)?.add(b)
    neighbours.get(b)?.add(a)
  }
  return { stationBySlug, neighbours }
}
